<template>
  <div>
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <CBreadcrumb v-bind="{ routes }" disableGoToBack />
    </Teleport>
    <div class="grid lg:grid-cols-2 gap-2 lg:gap-4 mb-5">
      <CWallet type="balance" @handleShowApplication="showApplication = true" />
      <CWallet type="referral" @handleShowTransfer="showTransfer = true" />
    </div>
    <CCard :class="[paginationData?.total > 5 ? 'p-5' : 'px-5 pt-5 pb-2']">
      <CTableWrapper
        :title="$t('history_transactions')"
        :subtitle="
          paginationData
            ? t('transactions_count', { count: paginationData.total })
            : ''
        "
        :data="tableData"
        :head="walletHead"
        :items-per-page="paginationData?.defaultLimit ?? 0"
        :limit="paginationData?.defaultLimit ?? 0"
        :current-page="paginationData?.currentPage ?? 0"
        :total="paginationData?.total"
        @search="onSearch"
        body-tr-class="hover:bg-transparent !cursor-default"
        @itemsPerPage="onChangeLimit"
        :loading="loading"
        @pageChange="onPageChange"
        th-class="!text-left first:!w-[400px] first:!min-w-[8px]"
      >
        <template #afterSearch>
          <CButton
            variant="transparent"
            size="sm"
            icon="icon-filter text-gray-450 text-lg font-medium"
            icon-position="left"
            :class="{ '!border-green': filterCount }"
            class="text-gray-450 mt-3 sm:mt-0"
            :text="$t('filter')"
            @click="showFilter = true"
          />
        </template>
        <template #id="{ row: data }">
          <span class="font-semibold text-sm">{{ data?.id }}</span>
        </template>
        <template #date="{ row: data }">
          {{ convertToUserTimezone(data?.created_at) }}
        </template>
        <template #status="{ row: data }">
          <CBadge
            v-if="data?.status === 'success'"
            :color="'green'"
            class="!bg-transparent font-medium !px-0"
            :icon-name="'icon-tick-circle'"
            :status="data?.status === 'success' ? $t('success') : ''"
          />
          <CBadge
            v-else
            :color="'red'"
            class="!bg-transparent !px-0 font-medium"
            :icon-name="'icon-forbidden'"
            :status="$t('fail')"
          />
        </template>
        <template #type="{ row: data }">
          <span class="capitalize">{{ data?.type }}</span>
        </template>
        <template #category="{ row: data }">
          <span
            class="uppercase"
            :class="{ 'text-green': data?.category === 'cr' }"
            >{{ data?.category }}</span
          >
        </template>
        <template #amount="{ row: data }">
          <span class="text-sm">
            <template v-if="data?.category === 'dr'">
              -{{ formatMoneyExact(data?.amount, 5) }}
            </template>
            <template v-else>
              {{ formatMoneyExact(data?.amount, 5) }}
            </template>
          </span>
        </template>
        <template #comment="{ row: data }">
          <span class="text-sm">{{
            data?.comment ? data?.comment : $t("no_comment")
          }}</span>
        </template>
        <template #no-data>
          <CNodata
            main-class="min-h-[226px]"
            image="/images/svg/no-data/no-transaction.svg"
            :title="$t('no_transactions')"
            :subtitle="$t('no_transactions_text')"
          />
        </template>
      </CTableWrapper>
    </CCard>
    <TransferDialog
      :show="showTransfer"
      @close="showTransfer = false"
      @updateTransactions="fetchTableData"
    />
    <TransferAmountDialog
      :data="transferData"
      :show="showAmountTransfer"
      @close="closeAmountTransferModal"
    />
    <CApplication
      :categories="transactionCategories"
      :show="showApplication"
      @close="closeModal"
      @showInfoTerms="showTerms = true"
      @handleTransferAmount="transferAmount"
    />
    <TermsInfoDialog
      :data="single"
      :show="showTerms"
      @close="showTerms = false"
    />
    <FilterDialog
      :form="form"
      :show="showFilter"
      @close="showFilter = false"
      @filter-transactions="handleTransactionFilter"
    />
  </div>
</template>

<script setup lang="ts">
import TransferAmountDialog from "@/modules/Wallet/components/TransferAmountDialog.vue";
import CTableWrapper from "@/components/Common/Table/CTableWrapper.vue";
import CCard from "@/components/Card/CardWrapper.vue";
import { walletHead } from "@/modules/Wallet/data";
import { formatMoneyExact } from "@/utils";
import CNodata from "@/components/Common/CNodata.vue";
import CWallet from "@/modules/Wallet/components/CWallet.vue";
import { useTableFetch } from "@/composables/useTableFetch";
import { useI18n } from "vue-i18n";
import CBadge from "@/components/Common/CBadge.vue";
import { computed, onMounted, ref, watch } from "vue";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useMounted } from "@/composables/useMounted";
import CButton from "@/components/Common/CButton.vue";
import FilterDialog from "@/modules/Wallet/components/FilterDialog.vue";
import { useForm } from "@/composables/useForm";
import TransferDialog from "@/modules/Wallet/components/TransferDialog.vue";
import CApplication from "@/modules/Wallet/components/CApplication.vue";
import ApiService from "@/services/ApiService";
import TermsInfoDialog from "@/components/Common/Dialog/TermsInfoDialog.vue";
import { useConvertToUserTimezone } from "@/composables/useConvertToUserTimezone";
import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const { t, locale } = useI18n();
const { convertToUserTimezone } = useConvertToUserTimezone();

const showFilter = ref(false);
const showApplication = ref(false);
const showTerms = ref(false);
const showTransfer = ref(false);
const showAmountTransfer = ref(false);
const transferData = ref({});

const transactionCategories = ref([]);
const { mounted } = useMounted();

const {
  tableData,
  paginationData,
  onSearch,
  onPageChange,
  onChangeLimit,
  loading,
  onFilter,
  fetchTableData,
} = useTableFetch("/payment/transactions/");

const routes = computed(() => [
  {
    name: t("wallet"),
    route: "/wallet",
  },
]);

const form = useForm(
  {
    status: "",
    type: "",
    category: "",
    date: "",
  },
  {},
);

const filterCount = ref(0);
const handleTransactionFilter = () => {
  const filteredObj = Object.fromEntries(
    Object.entries(form.values).filter(
      ([key, value]) => value !== "" && value !== null && value !== undefined,
    ),
  );
  if (!form.values.date) {
    onFilter("date", "");
  }
  filterCount.value = Object.values(filteredObj)?.length;
  if (filteredObj) {
    for (let el in filteredObj) {
      onFilter(
        el,
        typeof filteredObj[el] !== "string" || filteredObj[el] === "all"
          ? ""
          : filteredObj[el],
      );
    }
  }
  showFilter.value = false;
};

const transferAmount = (transfer: {
  id: number;
  address: string;
  icon: string;
  qr_code: string;
  title: string;
  amount: number;
  amountWithCommission: number;
}) => {
  transferData.value = transfer;
  showApplication.value = false;
  showAmountTransfer.value = true;
};

const fetchTransactionCategories = () => {
  ApiService.get("/payment/fill-balance-categories/").then((res) => {
    transactionCategories.value = res?.data?.results;
  });
};

const closeAmountTransferModal = () => {
  showAmountTransfer.value = false;
  fetchTableData();
};

const single = ref("");
function getSingle() {
  ApiService.get(`common/static/terms-conditions/`)
    .then((res) => {
      single.value = res.data;
    })
    .catch((e) => console.log(e.message));
}
const closeModal = () => {
  showApplication.value = false;
};
watch(
  () => showApplication.value,
  (val: boolean) => {
    if (val) {
      getSingle();
    }
  },
  { immediate: true, deep: true },
);
watch(
  () => locale.value,
  () => {
    fetchTableData();
  },
);
onMounted(() => {
  fetchTransactionCategories();
  pushToDataLayer("pageview", {
    page: "wallet",
    event_category: "page",
    event_label: "wallet_page_view",
    button_text: "None",
  });
});
</script>
